// Single-page master/detail explorer. Composes the rail + toolbar + table +
// detail panel. URL sync (deep-linkable filter state) is deferred to next
// batch.
import { useEffect } from "react";
import { describeApiError, EMPTY_FILTER_GROUP } from "../lib/api";
import { AppHeader } from "../components/AppHeader";
import { useAuthUser } from "../components/AuthGate";
import { FilterRail } from "../components/FilterRail";
import { AnaMoniChat } from "../components/AnaMoniChat";
import { ActiveFilterChips } from "../components/ActiveFilterChips";
import { ResultsTable } from "../components/ResultsTable";
import { AggregateView } from "../components/AggregateView";
import { ContactDetailPanel } from "../components/ContactDetailPanel";
import { AdvancedFilterModal } from "../components/AdvancedFilterModal";
import { useContacts } from "../hooks/useContacts";
import { useTheme } from "../hooks/useTheme";
import { useSnapshotStatus } from "../hooks/useSnapshotStatus";
import { useFields } from "../hooks/useFields";
import { useExplorerStore } from "../store/explorerStore";
import { useAnaMoniStore } from "../store/anaMoniStore";
import type { FilterGroup } from "../types/inpact";

export function ExplorerView() {
  useTheme();
  const { user } = useAuthUser();
  const { filterGroup, page, pageSize } = useExplorerStore();
  const advancedFilterOpen = useExplorerStore((s) => s.advancedFilterOpen);
  const advancedFilterSeed = useExplorerStore((s) => s.advancedFilterSeed);
  const closeAdvancedFilter = useExplorerStore((s) => s.closeAdvancedFilter);
  const setFilterGroup = useExplorerStore((s) => s.setFilterGroup);
  const setLastFilter = useAnaMoniStore((s) => s.setLastFilter);
  const snapshot = useSnapshotStatus();
  const {
    defaultFilterGroup,
    isLoading: policyLoading,
    error: policyError,
  } = useFields();
  const policyDefaultInitialized = useExplorerStore(
    (s) => s.policyDefaultInitialized,
  );
  const initializePolicyDefault = useExplorerStore((s) => s.initializePolicyDefault);
  useEffect(() => {
    if (!policyLoading && !policyError) {
      initializePolicyDefault(defaultFilterGroup ?? EMPTY_FILTER_GROUP);
    }
  }, [defaultFilterGroup, initializePolicyDefault, policyError, policyLoading]);

  // A builder session seeded from an AnaMoni bubble (the chat's "Edit in
  // builder" CTA — see AnaMoniChat.tsx) is the user editing THAT chat
  // answer's filter. Applying it must also become the chat's refinement
  // base, or the next follow-up ("now only Spain") would refine the
  // pre-edit filter and silently discard the edit. A session opened with no
  // seed (the rail's plain "Advanced filter" button) is editing the live
  // explorer state directly and isn't tied to any chat turn, so it's
  // deliberately left out — same as every other rail facet edit, which
  // already never touches `lastFilter`.
  const applyAdvancedFilter = (next: FilterGroup) => {
    setFilterGroup(next);
    if (advancedFilterSeed !== null) {
      setLastFilter(next);
    }
  };

  const { rows, total, totalPages, isLoading, isFetching, error } = useContacts({
    filterGroup,
    page,
    pageSize,
    enabled: policyDefaultInitialized,
  });

  return (
    <div className="app-shell">
      <AppHeader
        user={user}
        snapshot={{
          status: snapshot.status,
          isLoading: snapshot.isLoading,
          isRefreshing: snapshot.isRefreshing,
          statusError: snapshot.statusError,
          refreshError: snapshot.refreshError,
          onRefresh: () => { void snapshot.refresh(); },
        }}
      />

      <div className="app-body">
        {policyDefaultInitialized ? (
          <FilterRail />
        ) : (
          <aside aria-label="Filters unavailable until access policy loads" />
        )}

        <main className="main-area">
          <div>
            <h1 className="page-title">Contacts</h1>
            <p className="page-subtitle">
              All INPACT Candidates and Associates across consortium centres.
            </p>
          </div>

          {!policyDefaultInitialized && policyLoading && (
            <p role="status">Loading access policy…</p>
          )}
          {!policyDefaultInitialized && policyError && (
            <div role="alert">{describeApiError(policyError)}</div>
          )}

          {policyDefaultInitialized && <AnaMoniChat />}

          {policyDefaultInitialized && <ActiveFilterChips />}

          {error && (
            <div
              role="alert"
              style={{
                background: "var(--color-danger-100)",
                color: "var(--color-danger-600)",
                borderLeft: "4px solid var(--color-danger-600)",
                borderRadius: "var(--radius-md)",
                padding: "var(--space-3) var(--space-4)",
                fontSize: "var(--font-size-sm)",
              }}
            >
              {describeApiError(error)}
            </div>
          )}

          {policyDefaultInitialized && <AggregateView rows={rows} total={total} />}

          {policyDefaultInitialized && (
            <ResultsTable
              rows={rows}
              total={total}
              totalPages={totalPages}
              loading={isLoading || isFetching}
            />
          )}
        </main>
      </div>

      {policyDefaultInitialized && (
        <AdvancedFilterModal
          open={advancedFilterOpen}
          initial={advancedFilterSeed ?? filterGroup}
          onClose={closeAdvancedFilter}
          onApply={applyAdvancedFilter}
        />
      )}

      {policyDefaultInitialized && <ContactDetailPanel rows={rows} />}
    </div>
  );
}
